export const StyleService = {
  loadedFonts: new Set(),

  async loadAndApplyFont(fontName, fontLink, weight = null) {
    log.info(1,"🌜 StyleService loadAndApplyFont", fontName, fontLink, weight)
    
    // Auto-enhance Google Fonts URLs with multiple weights
    const enhancedFontLink = this.enhanceGoogleFontsUrl(fontLink)
    
    const loaded = await this.loadFont(fontName, enhancedFontLink, weight)
    if (loaded) {
      setTimeout(() => {
        this.setFontName(fontName)
        this.verifyFontApplication(fontName)
      }, 100)
    }
  },

  enhanceGoogleFontsUrl(fontLink) {
    if (!fontLink || typeof fontLink !== 'string') {
      return fontLink
    }

    // Check if this is a direct Google Fonts static file (gstatic.com)
    if (fontLink.includes('fonts.gstatic.com')) {
      log.info(1,"🌜 Direct Google Font file detected, converting to API URL")
      return this.convertGstaticToApiUrl(fontLink)
    }

    // Check if this is a Google Fonts API URL
    if (!fontLink.includes('fonts.googleapis.com')) {
      return fontLink
    }

    // Check if weights are already specified
    if (fontLink.includes(':wght@') || fontLink.includes(':ital,wght@')) {
      log.info(1,"🌜 Google Fonts URL already has weights specified")
      return fontLink
    }

    // Parse the URL
    try {
      const url = new URL(fontLink)
      const params = new URLSearchParams(url.search)
      const family = params.get('family')
      
      if (!family) {
        return fontLink
      }

      // Extract just the font name (before any colon)
      const fontNameMatch = family.match(/^([^:]+)/)
      if (!fontNameMatch) {
        return fontLink
      }
      
      const fontName = fontNameMatch[1]
      
      // Common weights for most use cases
      // 300 (Light), 400 (Regular), 500 (Medium), 600 (Semi-Bold), 700 (Bold), 800 (Extra-Bold)
      const weights = '300;400;500;600;700;800'
      
      // Check if the font might support italics
      const hasItalics = family.includes('ital') || fontLink.includes('ital')
      
      // Construct the enhanced family parameter
      let enhancedFamily
      if (hasItalics) {
        // Include both normal and italic variants
        enhancedFamily = `${fontName}:ital,wght@0,${weights};1,${weights}`
      } else {
        // Just normal weights
        enhancedFamily = `${fontName}:wght@${weights}`
      }
      
      params.set('family', enhancedFamily)
      url.search = params.toString()
      
      const enhancedUrl = url.toString()
      log.info(1,"🌜 Enhanced Google Fonts URL from:", fontLink)
      log.info(1,"🌜 Enhanced Google Fonts URL to:", enhancedUrl)
      
      return enhancedUrl
    } catch (error) {
      log.error('Error enhancing Google Fonts URL:', error)
      return fontLink
    }
  },

  convertGstaticToApiUrl(gstaticUrl) {
    try {
      // Extract font name from the URL path
      // Pattern: /s/fontname/version/file.ttf
      const urlParts = gstaticUrl.split('/')
      const fontNameIndex = urlParts.findIndex(part => part === 's') + 1
      
      if (fontNameIndex > 0 && fontNameIndex < urlParts.length) {
        let fontName = urlParts[fontNameIndex]
        
        // Convert to proper case if needed (montserrat -> Montserrat)
        fontName = fontName.charAt(0).toUpperCase() + fontName.slice(1)
        
        // Handle special cases for font names with multiple words
        // Convert camelCase or lowercase to proper spacing
        fontName = fontName.replace(/([A-Z])/g, ' $1').trim()
        if (fontName.includes(' ')) {
          fontName = fontName.split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join('+')
        }
        
        // Create a Google Fonts API URL with multiple weights
        const apiUrl = `https://fonts.googleapis.com/css2?family=${fontName}:wght@300;400;500;600;700;800&display=swap`
        
        log.info(1,"🌜 Converted gstatic URL to API URL:", apiUrl)
        return apiUrl
      }
      
      // If we can't parse it, return the original
      return gstaticUrl
    } catch (error) {
      log.error('Error converting gstatic URL:', error)
      return gstaticUrl
    }
  },

  async setFontName(fontName) {
    // Apply to player wrapper and media player
    const playerWrapper = document.getElementById("player-wrapper")
    if (playerWrapper) {
      playerWrapper.style.fontFamily = `'${fontName}', sans-serif`
    }
    const mediaPlayer = document.getElementById("media-player")
    if (mediaPlayer) {
      mediaPlayer.style.fontFamily = `'${fontName}', sans-serif`
    }
    // Apply to fixed wrapper layer for annotations
    const fixedWrapper = document.getElementById("fixed-wrapper-layer")
    if (fixedWrapper) {
      fixedWrapper.style.fontFamily = `'${fontName}', sans-serif`
    }
    // Apply to existing containers for editor/forms
    const playerContainer = document.getElementById("player-container")
    if (playerContainer) {
      playerContainer.style.fontFamily = `'${fontName}', sans-serif`
    }
    const playbackContainer = document.getElementById("playback-container")
    if (playbackContainer) {
      playbackContainer.style.fontFamily = `'${fontName}', sans-serif`
    }
    // Apply globally to text-content class
    const existingStyle = document.getElementById('custom-font-style')
    if (existingStyle) {
      existingStyle.remove()
    }
    const style = document.createElement('style')
    style.id = 'custom-font-style'
    style.textContent = `
      .text-content, .text-content * {
        font-family: '${fontName}', sans-serif !important;
      }
    `
    document.head.appendChild(style)
  },

  async loadFont(fontName, fontLink, weight = null) {
    log.info(1,"🌜 StyleService loadFont", fontName, fontLink, weight)
    if (!fontName || !fontLink) {
      log.info(1,"🌜 StyleService loadFont", fontName, fontLink, "not loaded")
      return false
    }

    try {
      // Create unique key for font variation
      const fontKey = weight ? `${fontName}-${weight}` : `${fontName}-all`
      
      // Check if font variation is already loaded
      if (this.loadedFonts.has(fontKey)) {
        log.info(1,`🔄 Font '${fontName}' weight '${weight || 'all'}' already loaded`)
        return true
      }

      // Check if this is a CSS stylesheet (Google Fonts style)
      if (fontLink.includes('css2') || fontLink.includes('css')) {
        await this.loadFontFromStylesheet(fontName, fontLink)
      } else {
        // Direct font file with optional weight
        await this.loadFontFromFile(fontName, fontLink, weight)
      }

      this.loadedFonts.add(fontKey)
      log.info(1,"🌜 StyleService loadFont", fontName, weight || 'all weights', "loaded successfully")
      return true
    } catch (error) {
      log.error('❌ Error loading font:', error)
      return false
    }
  },

  async loadFontFromFile(fontName, fontLink, weight = null) {
    try {
      // Map weight strings to CSS weight values
      const weightMap = {
        'thin': '100',
        'extralight': '200',
        'light': '300',
        'regular': '400',
        'medium': '500',
        'semibold': '600',
        'bold': '700',
        'extrabold': '800',
        'black': '900'
      }
      
      const fontOptions = {
        display: 'swap'
      }
      
      if (weight) {
        fontOptions.weight = weightMap[weight] || '400'
      }
      
      const font = new FontFace(fontName, `url(${fontLink})`, fontOptions)
      await font.load()
      document.fonts.add(font)
      log.info(1,`✅ Font file loaded: ${fontName} from ${fontLink} with weight ${weight || 'default'}`)
    } catch (error) {
      log.error(`❌ Failed to load font file ${fontName}:`, error)
      throw error
    }
  },

  async loadFontFromStylesheet(fontName, stylesheetUrl) {
    // Fetch the stylesheet content
    const response = await fetch(stylesheetUrl)
    const css = await response.text()

    // Parse the CSS to find all @font-face declarations
    const fontFaceRules = this.parseFontFaceRules(css)

    // Load each font variation
    const loadPromises = fontFaceRules.map(async rule => {
      try {
        // Create FontFace instance with the parsed properties
        const font = new FontFace(
          fontName,
          rule.src,
          {
            weight: rule.weight,
            style: rule.style,
            unicodeRange: rule.unicodeRange,
            display: rule.display
          }
        )

        await font.load()
        document.fonts.add(font)
      } catch (error) {
        log.info(1,'Error loading font variation:', error)
      }
    })

    await Promise.all(loadPromises)
  },

  parseFontFaceRules(css) {
    const rules = []
    const fontFaceRegex = /@font-face\s*{([^}]*)}/g
    const propertyRegex = /([a-zA-Z-]+)\s*:\s*([^;]+);/g

    let match
    while ((match = fontFaceRegex.exec(css)) !== null) {
      const ruleContent = match[1]
      const rule = {}
      let propertyMatch

      while ((propertyMatch = propertyRegex.exec(ruleContent)) !== null) {
        const [, property, value] = propertyMatch
        
        switch (property) {
          case 'src':
            // Extract URL and format from src
            const urlMatch = value.match(/url\(([^)]+)\)\s*format\('?([^)']+)'?\)/)
            if (urlMatch) {
              rule.src = `url(${urlMatch[1]})`
              rule.format = urlMatch[2]
            }
            break
          case 'font-weight':
            rule.weight = value
            break
          case 'font-style':
            rule.style = value
            break
          case 'unicode-range':
            rule.unicodeRange = value
            break
          case 'font-display':
            rule.display = value
            break
        }
      }

      if (rule.src) {
        rules.push(rule)
      }
    }

    return rules
  },

  clearLoadedFonts() {
    this.loadedFonts.clear()
  },

  isLoaded(fontName) {
    return this.loadedFonts.has(fontName)
  },

  verifyFontApplication(fontName) {
    // Check if font is actually loaded in browser
    const isFontAvailable = document.fonts.check(`12px '${fontName}'`)
    
    if (!isFontAvailable) {
      log.warn(`⚠️ Font '${fontName}' not available in browser despite loading attempt`)
      
      // Try alternative loading method
      const testDiv = document.createElement('div')
      testDiv.style.fontFamily = `'${fontName}', monospace`
      testDiv.style.position = 'absolute'
      testDiv.style.left = '-9999px'
      testDiv.textContent = 'Test'
      document.body.appendChild(testDiv)
      
      const computedFont = window.getComputedStyle(testDiv).fontFamily
      document.body.removeChild(testDiv)
      
      if (!computedFont.includes(fontName)) {
        log.error(`❌ Font '${fontName}' failed to apply. Computed: ${computedFont}`)
      } else {
        log.info(1,`✅ Font '${fontName}' successfully verified`)
      }
    } else {
      log.info(1,`✅ Font '${fontName}' is available and loaded`)
    }
    
    // Debug: List all loaded fonts
    if (window.VITE_DEVELOPMENT) {
      const loadedFonts = []
      document.fonts.forEach(font => {
        loadedFonts.push(`${font.family} - ${font.weight} - ${font.style}`)
      })
      log.info(1,"📄 All loaded fonts:", loadedFonts)
    }
  }
}